import React from 'react';
import PropTypes from 'prop-types';
import path from 'path';

import {oeInterfaceManager} from '../../react-oe/oe-interface';
import OEInterfaceAdapter from '../../react-oe/oe-interface-adapter';
import {OEAppInitResultCode, OEAppStatusErrorCode, OEAppStatusError, OEChangeTargetResult, OELanguageType, OEAppResetFlags} from '../../react-oe/oe-types';
import {AppInterfaceErrorTypes, AppInterfaceErrorReasons, AppInterfaceError, AppInterfaceResult, AppInterfaceEvents} from './app-interface-types';
import appInterfaceHelper from './app-interface-helper';
import {OEShowInitiallyFlags} from '../../lib/oe-types';
import {OEMediaCenterAutoplayMode} from '../../components/controller/media-center/oe-media-center-model';
import {OEURLParameter, OEURLParameterTool} from '../../lib/tools/oe-url-parameter-tool.js';
import urlQueries from '../../config/url-queries.js';
import SafeList from '../../lib/safe-list';
import {UpdateRetarder} from '../../lib/react/update-retarder.js';

export default class AppInterfaceThieme extends React.PureComponent {

    constructor(props){
        super(props);

        appInterfaceHelper.verbose = true

        this.oe = oeInterfaceManager.getInterface(this.props.moduleId);

        this.props.appComponent.appInterface = this;

        this._setFromURLQueryBlocked = 0;

        this._eventListener = {
            [AppInterfaceEvents.blockedChange]:           new SafeList()
        };
    }

    render()    {
        return <OEInterfaceAdapter moduleId={this.props.moduleId} receiver={this}/>;
    }

    addEventListener(eventName, fn) {
        if(typeof(eventName) !== 'string' || typeof(fn) !== 'function') return;
        if(!this._eventListener[eventName]) {
            console.log('Warning - trying to register listener to unknown event - ' + eventName + ' - in app interface.');
            return;
        }
        this._eventListener[eventName].addUnique(fn);
    }

    removeEventListener(eventName, fn) {
        if(typeof(eventName) !== 'string' || typeof(fn) !== 'function') return;
        if(!this._eventListener[eventName]) {
            console.log('Warning - trying to unregister listener from unknown event - ' + eventName + ' - in app interface.');
            return;
        }
        this._eventListener[eventName].remove(fn);
    }

    onSetFromURLQueryBlockedChanged(blocked)    {
        //console.log('AppInterfaceThieme - onSetFromURLQueryBlockedChanged - ' + blocked.toString());
        this._eventListener[AppInterfaceEvents.blockedChange].forEach((callback) => callback(this, {type: AppInterfaceEvents.blockedChange, blocked: blocked}));
    }

    // interface methods
    // ----------------------------------------------------------------------------------------------------------

    // checks if the app is ready to accept state changes
    isReady()   {
        return new Promise((resolve, reject) => {
            if(this.oe.isReady())    {
                resolve();
                return;
            }

            if(!this.oe.isInitializing())    {
                let abort = !this.oe.isPreReady();
                if(!abort)  {
                    let status = this.oe.Module.statusManager.getStatus();
                    const ModuleStatusType = this.oe.element.contentWindow.ModuleStatusType;
                    if(![ModuleStatusType.downloading, ModuleStatusType.downloadingData, ModuleStatusType.preparing, ModuleStatusType.running, ModuleStatusType.initializing, ModuleStatusType.initialized].includes(status.type))   abort = true;
                }
                if(abort)   {
                    reject(appInterfaceHelper.printError(new AppInterfaceError(AppInterfaceErrorTypes.std, AppInterfaceErrorReasons.notReady)));
                    return;
                }
            }

            this.oe.registerTargetChangeCallback((result) => {
                if(result.code !== OEAppInitResultCode.ok)   {
                    reject(appInterfaceHelper.printError(new AppInterfaceError(AppInterfaceErrorTypes.changeTarget, result)));
                    return;
                }
                resolve();
            });
        });
    }

    changeTarget(target)    {
        return new Promise((resolve, reject) => {
            if(typeof(target) !== 'string' || !target)    {
                reject(appInterfaceHelper.printError(new AppInterfaceError(
                    AppInterfaceErrorTypes.changeTarget,
                    new OEChangeTargetResult(OEAppInitResultCode.error, false, '', new OEAppStatusError(OEAppStatusErrorCode.unexpected))
                )));
                return;
            }

            if(!this.props.appComponent || !this.props.appComponent.app)    {
                reject(appInterfaceHelper.printError(new AppInterfaceError(
                    AppInterfaceErrorTypes.changeTarget,
                    new OEChangeTargetResult(OEAppInitResultCode.error, false, '', new OEAppStatusError(OEAppStatusErrorCode.notReady))
                )));
                return;
            }

            this.props.appComponent.app.changeTarget(target, (result, oe) => {
                if(result.code !== OEAppInitResultCode.ok)   {
                    reject(appInterfaceHelper.printError(new AppInterfaceError(
                        AppInterfaceErrorTypes.changeTarget,
                        result
                    )));
                    return;
                }

                resolve(result);
            })
        });
    }

    reset(animated = true, duration = 0.5, flags) {
        if(!this.oe.isReady())  return AppInterfaceResult.error(AppInterfaceErrorTypes.std, AppInterfaceErrorReasons.notReady);

        let app = this.oe.sharedInterface.getUIControllerApp();
        if(!app.getUIEnabled())   return AppInterfaceResult.error(AppInterfaceErrorTypes.std, AppInterfaceErrorReasons.uiBlock);

        this.props.appComponent.app.reset(animated, duration, flags);

        return AppInterfaceResult.ok();
    }

    resetP(animated = false, duration = 0.5, flags) {
        let result = this.reset(animated, duration, flags);
        return appInterfaceHelper._resolveRejectResultPromise(result);
    }

    setMainMenuOpen(open, animated)  {
        this.props.appComponent.uiLayer.showLeftSlideIn(open, animated);
        return AppInterfaceResult.ok();
    }

    setMainMenuOpentP(open, animated) {
        let result = this.setMainMenuOpen(open, animated);
        return appInterfaceHelper._resolveRejectResultPromise(result);
    }

    setShowTitleBar(show, animated)  {
        this.props.appComponent.capBar.show(show, animated);
        return AppInterfaceResult.ok();
    }

    setShowTitleBarP(show, animated)  {
        let result = this.setShowTitleBar(show, animated);
        return appInterfaceHelper._resolveRejectResultPromise(result);
    }

    setShowFullscreenBtn(show) {
        this.props.appComponent.appStateController.showFullscreenBtn(show);
        return AppInterfaceResult.ok();
    }

    setShowFullscreenBtnP(show) {
        let result = this.setShowFullscreenBtn(show);
        return appInterfaceHelper._resolveRejectResultPromise(result);
    }

    setFullscreen(fullscreen) {
        this.props.appComponent.appStateController.setFullscreen(fullscreen);
        return AppInterfaceResult.ok();
    }

    setFullscreenP(fullscreen) {
        let result = this.setFullscreen(fullscreen);
        return appInterfaceHelper._resolveRejectResultPromise(result);
    }

    setLanguage(code)   {
        let type = typeof(code) === 'string' ? OELanguageType.fromCode(code) : code;

        if(typeof(type) !== 'number' || type < 0)  return AppInterfaceResult.error(AppInterfaceErrorTypes.std, AppInterfaceErrorReasons.invalidParameter);

        if(!this.oe.isReady())  return AppInterfaceResult.error(AppInterfaceErrorTypes.std, AppInterfaceErrorReasons.notReady);

        let settings = this.oe.sharedInterface.getUIControllerSettings();
        if(!settings.getUIEnabled())  return AppInterfaceResult.error(AppInterfaceErrorTypes.std, AppInterfaceErrorReasons.uiBlock);
        
        settings.setLanguage({value: type});

        return AppInterfaceResult.ok();
    }

    setLanguageP(code) {
        let result = this.setLanguage(code);
        return appInterfaceHelper._resolveRejectResultPromise(result);
    }

    setContextMenuAvailable(available)  {
        if(!this.oe.isReady())  return AppInterfaceResult.error(AppInterfaceErrorTypes.std, AppInterfaceErrorReasons.notReady);

        let contextMenu = this.oe.sharedInterface.getUIControllerContextMenu();
        contextMenu.setAvailable(!!available);

        return AppInterfaceResult.ok();
    }

    setContextMenuAvailableP(available)  {
        let result = this.setContextMenuAvailable(available);
        return appInterfaceHelper._resolveRejectResultPromise(result);
    }

    setMediaCenterOpen(open)    {
        if(!this.oe.isReady())  return AppInterfaceResult.error(AppInterfaceErrorTypes.std, AppInterfaceErrorReasons.notReady);

        let res = this.props.appComponent.mediaCenter.setOpen(!!open);
 
        return AppInterfaceResult.ok(res);
    }

    setMediaCenterOpenP(open)    {
        let result = this.setMediaCenterOpen(open);
        return appInterfaceHelper._resolveRejectResultPromise(result);
    }

    setActiveMediaCenterCategory(index)  {
        if(typeof(index) !== 'number')  return AppInterfaceResult.error(AppInterfaceErrorTypes.std, AppInterfaceErrorReasons.invalidParameter);

        if(!this.oe.isReady())  return AppInterfaceResult.error(AppInterfaceErrorTypes.std, AppInterfaceErrorReasons.notReady);

        let mediaCenter = this.oe.sharedInterface.getUIControllerMediaCenter();
        
        if(!mediaCenter.getUIEnabled())   return AppInterfaceResult.error(AppInterfaceErrorTypes.std, AppInterfaceErrorReasons.uiBlock);

        let res = this.props.appComponent.mediaCenter.setActiveCategory(index);

        if(!res)  return AppInterfaceResult.error(AppInterfaceErrorTypes.std, AppInterfaceErrorReasons.couldNotFindItem);

        return AppInterfaceResult.ok(mediaCenter.getActualCategory());
    }

    setActiveMediaCenterCategoryP(index)    {
        let result = this.setActiveMediaCenterCategory(index);
        return appInterfaceHelper._resolveRejectResultPromise(result);
    }

    setActiveMediaCenterItem(identifier)  {
        if(typeof(identifier) !== 'string' && typeof(identifier) !== 'number')  return AppInterfaceResult.error(AppInterfaceErrorTypes.std, AppInterfaceErrorReasons.invalidParameter);

        if(!this.oe.isReady())  return AppInterfaceResult.error(AppInterfaceErrorTypes.std, AppInterfaceErrorReasons.notReady);

        let mediaCenter = this.oe.sharedInterface.getUIControllerMediaCenter();
        
        if(!mediaCenter.getUIEnabled())   return AppInterfaceResult.error(AppInterfaceErrorTypes.std, AppInterfaceErrorReasons.uiBlock);

        let res = this.props.appComponent.mediaCenter.setActiveMediaCenterItem(identifier);
        
        if(!res)  return AppInterfaceResult.error(AppInterfaceErrorTypes.std, AppInterfaceErrorReasons.couldNotFindItem);

        return AppInterfaceResult.ok(mediaCenter.getActualMediaItemID());
    }

    setActiveMediaCenterItemP(identifier)    {
        let result = this.setActiveMediaCenterItem(identifier);
        return appInterfaceHelper._resolveRejectResultPromise(result);
    }

    applyMediaCenterAutoplay(autoplay, save = false, update = true)    {
        if(!OEMediaCenterAutoplayMode.all.includes(autoplay))  return appInterfaceHelper._resolveRejectResultPromise(AppInterfaceResult.error(AppInterfaceErrorTypes.std, AppInterfaceErrorReasons.invalidParameter));

        if(!this.oe.isReady())  return appInterfaceHelper._resolveRejectResultPromise(AppInterfaceResult.error(AppInterfaceErrorTypes.std, AppInterfaceErrorReasons.notReady));

        let mediaCenter = this.oe.sharedInterface.getUIControllerMediaCenter();

        if(!mediaCenter.getUIEnabled())   return appInterfaceHelper._resolveRejectResultPromise(AppInterfaceResult.error(AppInterfaceErrorTypes.std, AppInterfaceErrorReasons.uiBlock));

        return this.props.appComponent.mediaCenter.applyAutoplay(autoplay, save, update);
    }

    setShowCatItemAutoplayMC(show, category, item, autoplay, save = false, update = true)   {
        let ret = this.setMediaCenterOpenP(show);
        if(!show)   return ret;
        if(category !== undefined)  ret = ret.then(res => this.setActiveMediaCenterCategoryP(category));
        if(item !== undefined)  ret = ret.then(res => this.setActiveMediaCenterItemP(item));
        if(autoplay !== undefined)  ret = ret.then(res => this.applyMediaCenterAutoplay(autoplay, save, update));
        return ret;
    }

    parseUILink(uiLink, encoded = true, filename = 'input string')   {
        if(!this.oe.isReady())  return AppInterfaceResult.error(AppInterfaceErrorTypes.std, AppInterfaceErrorReasons.notReady);

        let res = this.oe.sharedInterface.getUIControllerApp().loadUIState(uiLink, encoded, filename);
        if(res.code !== this.oe.Module.OEUIStateResultCode.ok)   return AppInterfaceResult.error(AppInterfaceErrorTypes.std, res.code === this.oe.Module.OEUIStateResultCode.fileCorrupt ? AppInterfaceErrorReasons.fileCorrupt : AppInterfaceErrorReasons.unexpected, filename);

        return AppInterfaceResult.ok(res.state);
    }

    parseUILinkP(uiLink, encoded = true, filename = 'input string')   {
        let result = this.parseUILink(uiLink, encoded, filename);
        return appInterfaceHelper._resolveRejectResultPromise(result);
    }

    loadUILinkRef(target, uiLinkRef, uiLinkRefPath = 'ui-states/', encoded = true)  {
        return appInterfaceHelper.downloadUILinkRef(this.oe, target, uiLinkRef, uiLinkRefPath).then(res => this.parseUILinkP(res.link, encoded, res.path));
    }

    applyUILink(uiLink, encoded = true, filename = 'input string')  {
        if(!this.oe.isReady())  return AppInterfaceResult.error(AppInterfaceErrorTypes.std, AppInterfaceErrorReasons.notReady);

        let resCode = this.oe.sharedInterface.getUIControllerApp().applyUILink(uiLink, encoded, filename);
        if(resCode !== this.oe.Module.OEUIStateResultCode.ok)   return AppInterfaceResult.error(AppInterfaceErrorTypes.std, resCode === this.oe.Module.OEUIStateResultCode.fileCorrupt ? AppInterfaceErrorReasons.fileCorrupt : AppInterfaceErrorReasons.unexpected, filename);

        return AppInterfaceResult.ok(resCode);
    }

    applyUILinkP(uiLink, encoded = true, filename = 'input string')  {
        let result = this.applyUILink(uiLink, encoded, filename);
        return appInterfaceHelper._resolveRejectResultPromise(result);
    }

    applyUILinkRef(target, uiLinkRef, uiLinkRefPath = 'ui-states/', encoded = true)  {
        return appInterfaceHelper.downloadUILinkRef(this.oe, target, uiLinkRef, uiLinkRefPath).then(res => this.applyUILinkP(res.link, encoded, res.path));
    }

    isSetFromURLQueryBlocked()  {
        return this._setFromURLQueryBlocked > 0;
    }

    /*
    Changes the app state with a given URL query.

    Supported URL parameters are: target, showMainMenu, showTitleBar, showFullscreenBtn, noContextMenu, uiLinkRef, autoplay

    For the definition of the parameters see the URL parameter documentation.

    This method tries to reproduce the state which would be applied if the app was started with the URL query with some limitations:

        1. The set of supported parameters is restricted.

        2. For showMainMenu, showTitleBar & showFullscreenBtn the extended behaviour for subsequent in app target changes can not be changed (see documentation and behaviour for target changes), e.g.,
           if the app is started with showMainMenu=4, the main menu will be closed after every in app target change, calling setFromURLQuery with showMainMenu=1 will open the main menu,
           but a further in app target change will close it again.
           In other words, this means that the in app target change behaviour has to be correctly set with the start URL and cannot be changed.

        3. There might occur further deviations not mentioned here.
           If such a deviation obstructs your use case, feel free to contact us!

    The return value is a promise which is rejected if and only if an error occurs.
    For the returned promise the onFulfilled parameter is the query and the onRejected parameter is an instance of AppInterfaceError, see also app-interface-types.
    If rejected an error description is written to the console out.
    */
    setFromURLQuery(query, defaults = undefined, options = undefined)  {
        options = Object.assign({reset: true, uiLinkRefPath: 'ui-states/', block: true, force: false}, options);
        defaults = Object.assign({showMainMenu: true, showTitleBar: true, showFullscreenBtn: true, noContextMenu: false}, defaults);

        if(!options.force && this._setFromURLQueryBlocked > 0)  return Promise.reject(appInterfaceHelper.printError(AppInterfaceError.error(AppInterfaceErrorTypes.std, AppInterfaceErrorReasons.blocked)));

        if(options.block)   {
            let change = !this.isSetFromURLQueryBlocked();
            this._setFromURLQueryBlocked++;
            if(change)  this.onSetFromURLQueryBlockedChanged(this.isSetFromURLQueryBlocked());
        }

        //
        let pTool = new OEURLParameterTool(urlQueries, query, false);
        let target = pTool.getValue(OEURLParameter.target);
        let showMainMenu = pTool.getValue(OEURLParameter.showMainMenu, defaults.showMainMenu);
        let showTitleBar = pTool.getValue(OEURLParameter.showTitleBar, defaults.showTitleBar);
        let showFullscreenBtn = pTool.getValue(OEURLParameter.showFullscreenBtn, defaults.showFullscreenBtn);
        let noContextMenu = pTool.getValue(OEURLParameter.noContextMenu, defaults.noContextMenu);
        let uiLinkRef = pTool.getValue(OEURLParameter.uiLinkRef);
        let autoplay = pTool.getValue(OEURLParameter.autoplay, OEMediaCenterAutoplayMode.none);

        //
        let intermediate = {
            targetChanged: false,
            uiLink: undefined
        };

        //
        let downloadUILinkRefPromise = uiLinkRef ? appInterfaceHelper.downloadUILinkRef(this.oe, target, uiLinkRef, options.uiLinkRefPath).then(res => {
            intermediate.uiLink = res
            return res;
        }) : undefined;

        //
        let ret = this.isReady();
        if(target)    {
            if(downloadUILinkRefPromise)    ret = ret.then(res => downloadUILinkRefPromise);

            ret = ret.then(res => this.changeTarget(target).then(res => {
                intermediate.targetChanged = res.changed;
                return res;
            }));
        }

        //
        if(!target && downloadUILinkRefPromise) ret = ret.then(res => downloadUILinkRefPromise);

        ret = ret.then(res => {

            return new Promise((resolve, reject) => {
                let result;

                intermediate.updateRetarder = new UpdateRetarder(this.props.appComponent.mediaCenter);

                if(options.reset && !intermediate.targetChanged)   {
                    let flags;
                    if(uiLinkRef && intermediate.uiLink) flags = OEAppResetFlags.reduced;

                    result = this.reset(true, 0.5, flags);
                    if(result.isError())    {
                        reject(appInterfaceHelper.printError(result.error));
                        return;
                    }
                }

                result = this.setMainMenuOpen(OEShowInitiallyFlags.resolve(showMainMenu).show);
                if(result.isError())    {
                    reject(appInterfaceHelper.printError(result.error));
                    return;
                }

                result = this.setShowTitleBar(OEShowInitiallyFlags.resolve(showTitleBar).show);
                if(result.isError())    {
                    reject(appInterfaceHelper.printError(result.error));
                    return;
                }

                result = this.setShowFullscreenBtn(OEShowInitiallyFlags.resolve(showFullscreenBtn).show);
                if(result.isError())    {
                    reject(appInterfaceHelper.printError(result.error));
                    return;
                }

                result = this.setContextMenuAvailable(!noContextMenu);
                if(result.isError())    {
                    reject(appInterfaceHelper.printError(result.error));
                    return;
                }

                if(uiLinkRef && intermediate.uiLink)    {
                    result = this.applyUILink(intermediate.uiLink.link, true, intermediate.uiLink.path);
                    if(result.isError())    {
                        reject(appInterfaceHelper.printError(result.error));
                        return;
                    }
                }

                if(intermediate.targetChanged && !OEMediaCenterAutoplayMode.isPlay(autoplay))   {
                    resolve(result.result);
                    return;
                }

                this.applyMediaCenterAutoplay(autoplay, intermediate.targetChanged || true, true).then(res => {
                    resolve(res);
                }, error => {
                    reject(error);
                });

            }).finally(() => { if(intermediate.updateRetarder)  intermediate.updateRetarder.finish(); });

        }).then(res => Promise.resolve(query));

        return ret.finally(() => {
            if(options.block)   this._setFromURLQueryBlocked = Math.max(0, this._setFromURLQueryBlocked - 1);
            if(!this.isSetFromURLQueryBlocked())    this.onSetFromURLQueryBlockedChanged(false);
        });
    }

    // ----------------------------------------------------------------------------------------------------------

    test_setFromURLQuery(i = 0) {
        let url = 'target=thiemeSkull&uiLinkRef=04030_e2.txt&autoplay=3';
        if(i == 1)  url = 'target=thiemeArm&uiLinkRef=04022_e2_.txt&autoplay=3';

        this.setFromURLQuery(url).then((res) => {
            console.log(res);
        }, (error) => {
            console.log(error);
        });
    }
}

AppInterfaceThieme.defaultProps = {
    moduleId: ''
};

AppInterfaceThieme.propTypes = {
    moduleId: PropTypes.string
};